import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import {
  Handshake, Target, TrendingUp, Copy, Check, Loader2, Euro, Users,
  Clock3, CheckCircle2, BadgeCheck, Lock, ArrowRight, Share2,
} from "lucide-react";

const fcfa = (n) => `${Math.round(Number(n || 0)).toLocaleString("fr-FR")} FCFA`;
const eur = (n) => `${Number(n || 0).toFixed(2)} €`;
const PERIOD_LABEL = { day: "du jour", week: "de la semaine", month: "du mois" };
const COMM_BADGE = {
  pending: { label: "En attente", cls: "bg-amber-50 text-amber-700" },
  validated: { label: "Validée", cls: "bg-sky-50 text-sky-700" },
  paid: { label: "Payée", cls: "bg-emerald-50 text-emerald-700" },
};

export default function Partenaire() {
  const { user } = useAuth();
  const [pub, setPub] = useState(null);
  const [mine, setMine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const refresh = async () => {
    try {
      const [p, m] = await Promise.all([
        api.get("/partners/public"),
        user ? api.get("/me/partner").catch(() => ({ data: null })) : Promise.resolve({ data: null }),
      ]);
      setPub(p.data);
      setMine(m.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [user]);

  if (loading) {
    return <div className="min-h-screen sl-hero-bg pt-28 grid place-items-center"><Loader2 className="animate-spin text-[var(--sl-primary)]" /></div>;
  }

  const progress = pub?.progress_percent || 0;
  const isPartner = mine?.partner && mine.partner.status === "approved";
  const isPending = mine?.partner && mine.partner.status === "pending";

  const copyLink = () => {
    if (!mine?.partner?.link) return;
    navigator.clipboard.writeText(mine.partner.link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
    toast.success("Lien partenaire copié !");
  };

  return (
    <div className="min-h-screen sl-hero-bg pt-24 sm:pt-28 pb-24">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <div className="text-center" data-testid="partner-public">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--sl-secondary)]/15 text-[var(--sl-secondary-dark)] mb-5"><Handshake size={28} /></div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{pub?.title || "Partenaire engagé"}</h1>
          <p className="mt-4 text-gray-600 max-w-xl mx-auto text-sm sm:text-base">
            Apportez des clients qui envoient de l'argent <span className="font-semibold">France → Gabon</span> et gagnez
            <span className="font-semibold text-emerald-700"> {pub?.commission_rate}% de commission</span> sur chaque transfert confirmé.
          </p>
        </div>

        {/* Objective progress */}
        <div className="mt-8 rounded-2xl bg-[var(--sl-primary)] text-white p-5 sm:p-6 shadow-lg" data-testid="partner-objective">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/70 mb-2">
            <Target size={14} /> Objectif {PERIOD_LABEL[pub?.objective_period] || "de la période"}
          </div>
          <div className="flex items-end justify-between gap-3 mb-3">
            <div className="text-2xl sm:text-3xl font-bold">{fcfa(pub?.realized_xaf)}</div>
            <div className="text-sm text-white/70">sur {fcfa(pub?.objective_amount)}</div>
          </div>
          <div className="h-2.5 rounded-full bg-white/20 overflow-hidden">
            <div className="h-full bg-[var(--sl-secondary)] transition-all" style={{ width: `${progress}%` }} />
          </div>
          <div className="mt-2 text-right text-sm text-white/80" data-testid="partner-progress">{progress}%</div>
        </div>

        {/* Conditions */}
        <div className="sl-card mt-4" data-testid="partner-conditions">
          <h3 className="font-heading font-semibold mb-2">Conditions du partenariat</h3>
          <p className="text-sm text-gray-600">{pub?.conditions}</p>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-emerald-50 text-emerald-700 px-3 py-1 font-medium">Commission {pub?.commission_rate}%</span>
            <span className="rounded-full bg-gray-100 text-gray-600 px-3 py-1 font-medium">France → Gabon</span>
            {Number(pub?.min_amount) > 0 && <span className="rounded-full bg-gray-100 text-gray-600 px-3 py-1 font-medium">Min. {eur(pub?.min_amount)}</span>}
          </div>
        </div>

        {/* Partner space / apply / closed */}
        {isPartner ? (
          <PartnerDashboard mine={mine} copyLink={copyLink} copied={copied} />
        ) : isPending ? (
          <div className="sl-card mt-6 text-center" data-testid="partner-pending">
            <Clock3 size={28} className="mx-auto text-amber-500 mb-2" />
            <h3 className="font-heading font-semibold">Candidature en cours d'examen</h3>
            <p className="text-sm text-gray-600 mt-1">Nous revenons vers vous très vite. Merci de votre intérêt !</p>
          </div>
        ) : !pub?.accepting_applications ? (
          <div className="sl-card mt-6 text-center" data-testid="partner-closed">
            <Lock size={28} className="mx-auto text-gray-400 mb-2" />
            <h3 className="font-heading font-semibold">Programme fermé</h3>
            <p className="text-sm text-gray-600 mt-1">{pub?.closed_message}</p>
          </div>
        ) : user ? (
          <ApplyForm onDone={refresh} rejected={mine?.partner?.status === "rejected"} reason={mine?.partner?.reason} />
        ) : (
          <div className="sl-card mt-6 text-center" data-testid="partner-login-cta">
            <p className="text-sm text-gray-600">Connectez-vous pour devenir partenaire engagé.</p>
            <div className="mt-3 flex items-center justify-center gap-3">
              <Link to="/login" className="sl-btn-primary text-sm">Connexion</Link>
              <Link to="/register" className="sl-btn-outline text-sm">Créer un compte</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ApplyForm({ onDone, rejected, reason }) {
  const [motivation, setMotivation] = useState("");
  const [phone, setPhone] = useState("");
  const [audience, setAudience] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/me/partner/apply", { motivation, phone, audience });
      toast.success("Candidature envoyée ! Nous l'examinons rapidement.");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <div className="sl-card mt-6" data-testid="partner-apply-form">
      <h3 className="font-heading font-semibold mb-1">Devenir partenaire</h3>
      {rejected && (
        <p className="text-xs text-rose-600 mb-2">Candidature précédente refusée{reason ? ` : ${reason}` : ""}. Vous pouvez postuler à nouveau.</p>
      )}
      <p className="text-sm text-gray-500 mb-4">Dites-nous comment vous comptez apporter des clients.</p>
      <div className="space-y-3">
        <div>
          <label className="sl-label">Téléphone (WhatsApp)</label>
          <input className="sl-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+241 / +33…" maxLength={20} data-testid="partner-phone" />
        </div>
        <div>
          <label className="sl-label">Votre audience / réseau</label>
          <input className="sl-input" value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="Ex: communauté diaspora, réseaux sociaux…" maxLength={160} data-testid="partner-audience" />
        </div>
        <div>
          <label className="sl-label">Motivation (optionnel)</label>
          <textarea className="sl-input min-h-[80px]" value={motivation} onChange={(e) => setMotivation(e.target.value)} maxLength={500} data-testid="partner-motivation" />
        </div>
        <button onClick={submit} disabled={saving} className="sl-btn-primary w-full justify-center" data-testid="partner-apply-submit">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Handshake size={16} />} Envoyer ma candidature
        </button>
      </div>
    </div>
  );
}

function PartnerDashboard({ mine, copyLink, copied }) {
  const s = mine.stats || {};
  return (
    <div className="mt-6 space-y-4" data-testid="partner-dashboard">
      <div className="sl-card bg-emerald-50/60 border-emerald-100">
        <div className="flex items-center gap-2 text-emerald-700 font-semibold mb-2"><BadgeCheck size={18} /> Vous êtes partenaire engagé</div>
        <label className="sl-label">Votre lien partenaire</label>
        <div className="flex items-center gap-2">
          <input readOnly value={mine.partner.link} className="sl-input flex-1 text-xs" data-testid="partner-link" />
          <button onClick={copyLink} className="sl-btn-primary text-xs shrink-0" data-testid="partner-copy-link">
            {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copié" : "Copier"}
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">Code : <span className="font-mono font-semibold">{mine.partner.code}</span> · Partagez ce lien ; chaque transfert France → Gabon confirmé vous rapporte {mine.program?.commission_rate}%.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Users} label="Clients apportés" value={s.clients || 0} testid="pstat-clients" />
        <Stat icon={TrendingUp} label="Transferts" value={s.tx_count || 0} testid="pstat-tx" />
        <Stat icon={Target} label="Volume généré" value={fcfa(s.volume_xaf)} small testid="pstat-volume" />
        <Stat icon={Euro} label="Commissions payées" value={eur(s.commission_paid)} small testid="pstat-paid" />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <MiniStat label="En attente" value={eur(s.commission_pending)} cls="text-amber-600" />
        <MiniStat label="Validées" value={eur(s.commission_validated)} cls="text-sky-600" />
        <MiniStat label="Payées" value={eur(s.commission_paid)} cls="text-emerald-600" />
      </div>

      <div className="sl-card" data-testid="partner-commissions">
        <h3 className="font-heading font-semibold mb-3">Historique des commissions</h3>
        {(mine.commissions || []).length === 0 ? (
          <p className="text-sm text-gray-400">Aucune commission pour l'instant. Partagez votre lien !</p>
        ) : (
          <div className="space-y-2">
            {mine.commissions.map((c) => {
              const b = COMM_BADGE[c.status] || COMM_BADGE.pending;
              return (
                <div key={c.id} className="flex items-center justify-between rounded-xl border px-3 py-2 text-sm" data-testid={`pcommission-${c.id}`}>
                  <div>
                    <div className="font-medium">{c.tx_code} · {eur(c.commission_amount)}</div>
                    <div className="text-xs text-gray-400">{(c.created_at || "").slice(0, 10)} · {fcfa(c.amount_xaf)}</div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${b.cls}`}>{b.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, small, testid }) {
  return (
    <div className="sl-card !p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-gray-400 mb-1"><Icon size={14} /><span className="text-[11px]">{label}</span></div>
      <div className={small ? "text-base font-bold" : "text-xl font-bold"}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value, cls }) {
  return (
    <div className="sl-card !p-3 text-center">
      <div className="text-[11px] text-gray-400">{label}</div>
      <div className={`text-sm font-bold ${cls}`}>{value}</div>
    </div>
  );
}
